<template>
  <Transition :name="gestureDismissed ? '' : 'drawer-slide'">
    <div v-if="isOpen" class="DrawerModal" :class="{ 'is-mobile': isMobile }" @click.self="handleClose">
      <div ref="drawerEl" class="settings-drawer">
        <div class="drawer-scroll">
          <div class="drawer-header">
            <GamIcon v-if="icon" :name="icon" is-current />
            <div v-if="title || subtitle" class="drawer-heading">
              <span v-if="title" class="drawer-title">{{ localize(title) }}</span>
              <template v-if="title && subtitle">
                <span class="drawer-separator">&middot;</span>
              </template>
              <span v-if="subtitle" class="drawer-subtitle">{{ localize(subtitle) }}</span>
            </div>
          </div>

          <div class="settings-content">
            <slot />
          </div>

          <div class="drawer-bottom">
            <slot name="footer">
              <div class="cta-circle" @click="handleClose">
                <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
              </div>
              <div
                v-if="hasChanges"
                class="cta-circle cta-check is-active"
                :class="{ 'is-disabled': isDisabled }"
                @click="handleSubmit"
              >
                <GamIcon v-if="!isLoading" :name="GamIconName.CHECK" :size="GamIconSize.X_LARGE" is-current />
                <div v-else class="cta-spinner" />
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { ref, toRef, watch } from 'vue';

interface DrawerModalProps {
  isOpen: boolean;
  title?: string;
  subtitle?: string;
  icon?: GamIconName;
  hasChanges?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const props = withDefaults(defineProps<DrawerModalProps>(), {
  title: undefined,
  subtitle: undefined,
  icon: undefined,
  hasChanges: false,
  isLoading: false,
  isDisabled: false,
});

const emit = defineEmits<{
  close: [];
  submit: [];
}>();

const drawerEl = ref<HTMLElement | null>(null);
const gestureDismissed = ref(false);

watch(
  () => props.isOpen,
  (open) => {
    if (open) gestureDismissed.value = false;
  },
);

useDrawerGesture(
  drawerEl,
  () => {
    gestureDismissed.value = true;
    emit('close');
  },
  { enabled: toRef(() => props.isOpen && !!isMobile.value) },
);

const handleClose = () => {
  if (props.isLoading) return;
  (document.activeElement as HTMLElement)?.blur();
  emit('close');
};

const handleSubmit = () => {
  if (props.isLoading || props.isDisabled) return;
  emit('submit');
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.DrawerModal {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);

  &.is-mobile {
    align-items: flex-end;
  }
}

.settings-drawer {
  width: 600px;
  max-width: calc(100vw - 2rem);
  max-height: min(720px, calc(100vh - 4rem));
  background: var(--color-dark-700);
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  overflow: hidden;

  .is-mobile & {
    width: 100%;
    max-width: 100%;
    max-height: calc(100vh - env(safe-area-inset-top) - 1rem);
    max-height: min(92dvh, calc(100dvh - env(safe-area-inset-top) - 1rem));
    border-radius: 1.5rem 1.5rem 0 0;
    border-bottom: none;
    will-change: transform;
  }
}

.drawer-scroll {
  width: 100%;
  max-height: inherit;
  overflow-y: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.drawer-header {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-md);
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--color-white-10);
  color: var(--color-white-70);
  background: var(--color-dark-700);
  z-index: 2;
}

.drawer-heading {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  min-width: 0;
}

.drawer-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-white);
  flex: none;
}

.drawer-separator {
  color: var(--color-white-30);
}

.drawer-subtitle {
  font-size: 12px;
  letter-spacing: 0.02em;
  color: var(--color-white-50);
}

.settings-content {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
}

.drawer-bottom {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  padding-bottom: max(1rem, var(--safe-bottom-offset));
  border-top: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  z-index: 2;
}

.cta-circle {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  &.cta-check.is-active {
    background-color: var(--color-pink);
    color: var(--color-white);

    @include hover {
      filter: brightness(1.1);
    }
  }

  &.is-disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  @include press;
}

.cta-spinner {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--color-white-30);
  border-top-color: var(--color-white);
  border-radius: 50%;
  animation: spin 600ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 200ms ease;

  .settings-drawer {
    transition: transform 200ms ease;
  }
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;

  .settings-drawer {
    transform: translateY(20px);
  }

  &.is-mobile .settings-drawer {
    transform: translateY(100%);
  }
}
</style>
